import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame } from "@/lib/icons";
import { playCrackle } from "@/lib/labSounds";
import { useStopAnimations } from "@/hooks/useStopAnimations";

interface MetalSalt {
  id: string;
  name: string;
  formula: string;
  flameColor: string;
  flameLabel: string;
  description: string;
}

const metalSalts: MetalSalt[] = [
  { id: "li", name: "Lithium chloride", formula: "LiCl", flameColor: "hsl(0, 85%, 55%)", flameLabel: "Crimson Red", description: "Lithium produces a deep crimson red flame, used in fireworks for red effects." },
  { id: "na", name: "Sodium chloride", formula: "NaCl", flameColor: "hsl(45, 95%, 55%)", flameLabel: "Bright Yellow", description: "Sodium's intense yellow flame dominates - even traces produce the characteristic color of street lamps." },
  { id: "k", name: "Potassium chloride", formula: "KCl", flameColor: "hsl(280, 60%, 55%)", flameLabel: "Lilac/Violet", description: "Potassium gives a delicate lilac flame, best viewed through cobalt blue glass to filter sodium." },
  { id: "ca", name: "Calcium chloride", formula: "CaCl₂", flameColor: "hsl(15, 90%, 55%)", flameLabel: "Orange-Red", description: "Calcium produces a brick-red to orange flame, distinguishable from lithium's deeper crimson." },
  { id: "sr", name: "Strontium chloride", formula: "SrCl₂", flameColor: "hsl(0, 90%, 50%)", flameLabel: "Scarlet Red", description: "Strontium burns with a vivid scarlet red - the classic color of red fireworks and road flares." },
  { id: "ba", name: "Barium chloride", formula: "BaCl₂", flameColor: "hsl(120, 60%, 50%)", flameLabel: "Apple Green", description: "Barium produces a distinctive pale green flame, used in green fireworks and signal flares." },
  { id: "cu", name: "Copper(II) chloride", formula: "CuCl₂", flameColor: "hsl(160, 80%, 45%)", flameLabel: "Blue-Green", description: "Copper salts produce a vibrant blue-green (turquoise) flame due to CuCl emission bands." },
  { id: "cs", name: "Caesium chloride", formula: "CsCl", flameColor: "hsl(240, 50%, 55%)", flameLabel: "Blue-Violet", description: "Caesium gives a blue-violet flame, similar to potassium but shifted further toward blue." },
  { id: "fe", name: "Iron filings", formula: "Fe", flameColor: "hsl(35, 80%, 50%)", flameLabel: "Golden Sparks", description: "Iron doesn't produce a clear flame color but creates golden-orange sparks when heated." },
  { id: "pb", name: "Lead nitrate", formula: "Pb(NO₃)₂", flameColor: "hsl(210, 20%, 65%)", flameLabel: "Pale Blue-White", description: "Lead gives a faint pale blue-white flame, historically used to identify lead in pigments." },
];

export default function FlameTest() {
  const [selected, setSelected] = useState<MetalSalt | null>(null);
  const [isBurning, setIsBurning] = useState(false);
  const reduceMotion = useStopAnimations();

  const handleTest = (salt: MetalSalt) => {
    setSelected(salt);
    setIsBurning(true);
    playCrackle();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      {/* Metal salt selector */}
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="text-sm font-medium text-foreground mb-1 flex items-center gap-2">
          <Flame size={14} className="text-primary" />
          Metal Salts
        </h3>
        <p className="text-[11px] text-muted-foreground mb-3">Select a salt to observe its flame color.</p>
        <div className="space-y-1 max-h-[500px] overflow-y-auto pr-1 -mr-1">
          {metalSalts.map((salt) => (
            <button
              key={salt.id}
              onClick={() => handleTest(salt)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border transition-all text-left ${
                selected?.id === salt.id
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:bg-accent/50 hover:border-primary/30"
              }`}
            >
              <div
                className="w-4 h-4 rounded-full border border-border/50 shadow-inner flex-shrink-0 transition-colors duration-500 ease-out"
                style={{
                  backgroundColor:
                    selected?.id === salt.id && isBurning
                      ? salt.flameColor
                      : "transparent",
                }}
              />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-mono font-medium text-foreground">{salt.formula}</span>
                <span className="text-[10px] text-muted-foreground block truncate">{salt.name}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Flame visualization */}
      <div className="rounded-xl border border-border bg-gradient-to-b from-card to-muted/30 p-6 flex flex-col items-center justify-center min-h-[450px] relative overflow-hidden">
        {/* Bunsen burner base */}
        <div className="relative w-40 h-72 flex flex-col items-center justify-end">
          {/* Flame */}
          <AnimatePresence mode="wait">
            {selected && isBurning && (
              <motion.div
                key={selected.id}
                initial={{ scaleY: 0, opacity: 0 }}
                animate={{ scaleY: 1, opacity: 1 }}
                exit={{ scaleY: 0, opacity: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="absolute bottom-[120px] flex flex-col items-center origin-bottom"
              >
                {/* Outer flame glow */}
                <motion.div
                  className="absolute w-28 h-36 rounded-full blur-xl"
                  style={{ backgroundColor: selected.flameColor, opacity: 0.3 }}
                  animate={
                    reduceMotion
                      ? { scale: 1, opacity: 0.3 }
                      : {
                          scale: [1, 1.1, 0.95, 1.05, 1],
                          opacity: [0.3, 0.4, 0.25, 0.35, 0.3],
                        }
                  }
                  transition={
                    reduceMotion
                      ? { duration: 0 }
                      : { duration: 2, repeat: Infinity, ease: "easeInOut" }
                  }
                />
                {/* Main flame */}
                <motion.svg
                  width="80"
                  height="130"
                  viewBox="0 0 80 130"
                  className="relative z-10"
                >
                  <defs>
                    <filter id="flameBlur">
                      <feGaussianBlur stdDeviation="2" />
                    </filter>
                    <radialGradient id={`flameGrad-${selected.id}`} cx="50%" cy="70%" r="50%">
                      <stop offset="0%" style={{ stopColor: "white", stopOpacity: 0.9 }} />
                      <stop offset="40%" style={{ stopColor: selected.flameColor, stopOpacity: 0.8 }} />
                      <stop offset="100%" style={{ stopColor: selected.flameColor, stopOpacity: 0 }} />
                    </radialGradient>
                  </defs>
                  <motion.path
                    d="M 40 5 Q 25 40 20 65 Q 15 90 25 110 Q 30 120 40 125 Q 50 120 55 110 Q 65 90 60 65 Q 55 40 40 5 Z"
                    fill={`url(#flameGrad-${selected.id})`}
                    filter="url(#flameBlur)"
                    animate={
                      reduceMotion
                        ? {}
                        : {
                            d: [
                              "M 40 5 Q 25 40 20 65 Q 15 90 25 110 Q 30 120 40 125 Q 50 120 55 110 Q 65 90 60 65 Q 55 40 40 5 Z",
                              "M 40 8 Q 22 38 18 62 Q 14 88 26 112 Q 32 122 40 126 Q 48 122 54 112 Q 66 88 62 62 Q 58 38 40 8 Z",
                              "M 40 3 Q 27 42 22 68 Q 16 92 24 108 Q 29 118 40 124 Q 51 118 56 108 Q 64 92 58 68 Q 53 42 40 3 Z",
                              "M 40 5 Q 25 40 20 65 Q 15 90 25 110 Q 30 120 40 125 Q 50 120 55 110 Q 65 90 60 65 Q 55 40 40 5 Z",
                            ],
                          }
                    }
                    transition={
                      reduceMotion
                        ? { duration: 0 }
                        : { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                    }
                  />
                  {/* Inner bright core */}
                  <motion.ellipse
                    cx="40"
                    cy="100"
                    rx="10"
                    ry="18"
                    fill="white"
                    opacity="0.4"
                    filter="url(#flameBlur)"
                    animate={
                      reduceMotion
                        ? { ry: 18, opacity: 0.4 }
                        : { ry: [18, 15, 20, 18], opacity: [0.4, 0.5, 0.35, 0.4] }
                    }
                    transition={
                      reduceMotion
                        ? { duration: 0 }
                        : { duration: 1.2, repeat: Infinity, ease: "easeInOut" }
                    }
                  />
                </motion.svg>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Burner body */}
          <svg viewBox="0 0 120 140" className="w-28 h-36 relative z-20" xmlns="http://www.w3.org/2000/svg">
            {/* Barrel */}
            <rect x="45" y="10" width="30" height="80" rx="2" fill="hsl(var(--muted))" stroke="hsl(var(--border))" strokeWidth="1.5" />
            {/* Air hole */}
            <ellipse cx="60" cy="70" rx="6" ry="3" fill="hsl(var(--background))" stroke="hsl(var(--border))" strokeWidth="1" />
            {/* Base */}
            <path d="M 25 90 L 45 90 L 45 130 L 25 130 Z" fill="hsl(var(--muted))" stroke="hsl(var(--border))" strokeWidth="1.5" rx="3" />
            <path d="M 75 90 L 95 90 L 95 130 L 75 130 Z" fill="hsl(var(--muted))" stroke="hsl(var(--border))" strokeWidth="1.5" rx="3" />
            <rect x="20" y="126" width="80" height="8" rx="3" fill="hsl(var(--muted-foreground))" opacity="0.3" />
            {/* Collar */}
            <rect x="42" y="85" width="36" height="10" rx="2" fill="hsl(var(--muted-foreground))" opacity="0.2" stroke="hsl(var(--border))" strokeWidth="1" />
            {/* Nozzle top */}
            <rect x="48" y="6" width="24" height="8" rx="2" fill="hsl(var(--muted))" stroke="hsl(var(--border))" strokeWidth="1.5" />
          </svg>
        </div>

        {/* Result info */}
        <AnimatePresence mode="wait">
          {selected && (
            <motion.div
              key={selected.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4 text-center max-w-sm"
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <div
                  className="w-4 h-4 rounded-full border border-border shadow-sm"
                  style={{ backgroundColor: selected.flameColor }}
                />
                <span className="text-sm font-semibold text-foreground">{selected.flameLabel}</span>
              </div>
              <p className="text-xs font-mono text-muted-foreground mb-1">{selected.formula} - {selected.name}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{selected.description}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {!selected && (
          <p className="text-sm text-muted-foreground mt-4">Select a metal salt to observe its characteristic flame color</p>
        )}
      </div>
    </div>
  );
}
